/**
 * Periodontograma (Especialidades → Periodoncia): registro de los seis sitios de cada diente con profundidad de sondaje, recesión,
 * sangrado, placa y supuración, más movilidad y furca. Se guarda cada vez como un registro nuevo e inalterable y se compara con el anterior.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FOTOS_DIENTES } from "../datos/dientesFotos.ts";
import { api, puede, type Paciente } from "../api.ts";
import {
  DIENTES_SUPERIORES, DIENTES_INFERIORES, periodontogramaVacio, limpiarPeriodontograma, resumenPeriodontograma, textoResumenPerio, compararPerio, problemasPeriodontograma, tieneFurca,
  type DatosPerio, type SitioPerio, type ResumenPerio,
} from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";
import { hoy } from "./comun.tsx";

interface Registro { id: string; fecha: string; datos: DatosPerio; resumen: ResumenPerio; observaciones: string; profesional: string }

const fechaLarga = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" });
const signo = (n: number | null, unidad: string) => (n === null ? "" : n === 0 ? `sin cambio en ${unidad}` : `${n < 0 ? "▼" : "▲"} ${n > 0 ? "+" : "−"}${Math.abs(n)} ${unidad}`);

/** Casilla numérica de una medida: acepta vacío (sin medir) y, si se permite, el signo menos. Enter pasa a la siguiente. */
function Num({ valor, alCambiar, min, max, etiqueta, bloqueado, clase }: { valor: number | null; alCambiar: (n: number | null) => void; min: number; max: number; etiqueta: string; bloqueado: boolean; clase?: string }) {
  const leer = (t: string): number | null => { const n = Number(t); return t.trim() === "" || !/^-?\d{1,2}$/.test(t.trim()) || n < min || n > max ? null : n; };
  const [t, setT] = useState(valor === null ? "" : String(valor));
  useEffect(() => { if (leer(t) !== valor) setT(valor === null ? "" : String(valor)); }, [valor]);
  return (
    <input className={`perio-n ${clase ?? ""}`} inputMode="numeric" maxLength={min < 0 ? 3 : 2} aria-label={etiqueta} value={t} disabled={bloqueado}
      onChange={(e) => { const x = e.target.value; if (x === "" || /^-?\d{0,2}$/.test(x)) { setT(x); alCambiar(leer(x)); } }}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        // Enter sube el valor de uno en uno; al pasar del máximo vuelve a 0. Con Shift se pasa a la casilla siguiente (lo atiende la tabla).
        if (e.key !== "Enter") return;
        e.preventDefault();
        const sig = (valor ?? 0) + 1;
        const n = sig > max ? 0 : sig;
        setT(String(n)); alCambiar(n);
      }} />
  );
}

const Marca = ({ activa, alCambiar, etiqueta, bloqueado, clase }: { activa: boolean; alCambiar: () => void; etiqueta: string; bloqueado: boolean; clase: string }) => (
  <button type="button" className={`perio-m ${clase}${activa ? " on" : ""}`} aria-pressed={activa} aria-label={etiqueta} disabled={bloqueado} onClick={alCambiar}><span className="perio-pt" /></button>
);

const NOMBRE_SITIO = ["mesial", "central", "distal"];

const VERDE = "#2e9e5b", MARRON = "#8b5a2b", ROJO = "#d13b3b", MARGEN = "#6b7280";
/** Color de un sitio: rojo = supuración o sondaje de 6 mm o más; marrón = sangrado o sondaje de 4 a 5 mm; verde = sin hallazgo. */
const colorSitio = (s: SitioPerio) => (s.sup || (s.pd ?? 0) >= 6 ? ROJO : s.sang || (s.pd ?? 0) >= 4 ? MARRON : VERDE);

/** Ancho de cada sitio (px) y medidas del dibujo de un diente. Todas las columnas de la tabla miden igual para que el dibujo calce con las casillas. */
const W = 26, ETIQ = 112;
/** Altura del dibujo, ubicación de la línea cervical (límite corona-raíz) y píxeles por milímetro: las fotos se recortaron a esa escala. */
const H = 162, CEJ = 100, ESC = 4.2;

/** Qué fotografía usa cada cara: la primera fila es siempre la vestibular y la segunda la palatina (superiores) o lingual (inferiores). */
const vistaFoto = (_n: number, segunda: boolean) => (segunda ? "palatina" : "vestibular");

/** En los dientes del lado derecho del paciente (cuadrantes 1 y 4, a la izquierda de la pantalla) lo mesial queda a la derecha. */
const ordenSitios = (n: number): number[] => (n < 20 || (n > 40 && n < 50) ? [2, 1, 0] : [0, 1, 2]);

/**
 * Una cara de la arcada dibujada sobre los dientes: línea gris = margen de la encía (recesión) y línea de color = fondo del surco (margen + sondaje),
 * verde sin hallazgo, marrón con sangrado o 4 a 5 mm, roja con supuración o 6 mm o más. Los superiores van con la raíz hacia arriba y los inferiores (raizAbajo) con la raíz hacia abajo.
 */
function DientesCara({ dientes, datos, cara, raizAbajo, segunda, nombre }: { dientes: readonly number[]; datos: DatosPerio; cara: "v" | "l"; raizAbajo: boolean; segunda: boolean; nombre: string }) {
  const ancho = dientes.length * 3 * W;
  const py = (v: number) => { const c = Math.max(2, Math.min(H - 2, v)); return raizAbajo ? H - c : c; };
  type P = { g: number; x: number; s: SitioPerio; n: number };
  const puntos: P[] = dientes.flatMap((n, ti) => (datos[n].ausente ? [] : ordenSitios(n).map((i, p) => ({ g: ti * 3 + p, x: (ti * 3 + p + 0.5) * W, s: datos[n][cara][i], n }))));
  const margen = puntos.filter((p) => p.s.pd !== null || p.s.rec !== null);
  const surco = puntos.filter((p) => p.s.pd !== null);
  const yMargen = (p: P) => py(CEJ - (p.s.rec ?? 0) * ESC);
  const ySurco = (p: P) => py(CEJ - ((p.s.rec ?? 0) + (p.s.pd ?? 0)) * ESC);
  const unidos = (l: P[]) => l.slice(1).map((b, j) => ({ a: l[j], b })).filter(({ a, b }) => b.g - a.g === 1);
  return (
    <svg width={ancho} height={H} role="img" aria-label={`Dibujo de los dientes, cara ${nombre}`} style={{ display: "block" }}>
      <rect width={ancho} height={H} fill="#fff" />
      <g>
        {dientes.map((n, ti) => {
          const vista = vistaFoto(n, segunda), [w2, h2, c2] = FOTOS_DIENTES[`${n}_${vista}`], w = w2 / 2, h = h2 / 2, cj = c2 / 2;
          // las fotos de los superiores traen la raíz hacia arriba y las de los inferiores hacia abajo: no se voltean
          const y0 = raizAbajo ? H - CEJ - cj : CEJ - cj;
          return (
            <image key={n} href={`/dientes/${n}_${vista}.jpg`} x={(ti * 3 + 1.5) * W - w / 2} y={y0} width={w} height={h} preserveAspectRatio="none"
              opacity={datos[n].ausente ? 0.2 : 1} style={{ mixBlendMode: "multiply" }} />
          );
        })}
      </g>
      {unidos(margen).map(({ a, b }) => <line key={`m${a.g}`} x1={a.x} y1={yMargen(a)} x2={b.x} y2={yMargen(b)} stroke={MARGEN} strokeWidth="1.6" strokeLinecap="round" />)}
      {unidos(surco).map(({ a, b }) => <line key={`s${a.g}`} x1={a.x} y1={ySurco(a)} x2={b.x} y2={ySurco(b)} stroke={colorSitio(b.s)} strokeWidth="2.6" strokeLinecap="round" />)}
      {margen.map((p) => <circle key={`pm${p.g}`} cx={p.x} cy={yMargen(p)} r="2.2" fill={MARGEN} />)}
      {surco.map((p) => (
        <circle key={`ps${p.g}`} cx={p.x} cy={ySurco(p)} r="3.4" fill={colorSitio(p.s)} stroke="#fff" strokeWidth="1">
          <title>{`Diente ${p.n}: sondaje ${p.s.pd} mm${p.s.rec ? ` · recesión ${p.s.rec} mm` : ""}${p.s.sang ? " · sangra" : ""}${p.s.sup ? " · supuración" : ""}`}</title>
        </circle>
      ))}
    </svg>
  );
}

function filasArcada({ dientes, datos, cambiar, bloqueado, inferior, cara: caraPedida }: { cara: "v" | "l"; dientes: readonly number[]; datos: DatosPerio; cambiar: (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => void; bloqueado: boolean; inferior: boolean }) {
  const nombreCara = caraPedida === "v" ? `Vestibular ${inferior ? "inferior" : "superior"}` : inferior ? "Lingual" : "Palatino";
  const primera: ["v" | "l", string] = [caraPedida, nombreCara];
  const k = inferior ? "i" : "s";
  const sitio = (n: number, cara: "v" | "l", i: number, f: (s: SitioPerio) => SitioPerio) => cambiar(n, (d) => ({ ...d, [cara]: d[cara].map((s, j) => (j === i ? f(s) : s)) }));
  const cols = dientes.length * 3;
  const celdas = (clave: string, f: (n: number, i: number) => ReactNode) =>
    dientes.flatMap((n) => ordenSitios(n).map((i) => <td key={`${clave}${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && f(n, i)}</td>));
  const fila = (clave: string, titulo: string, f: (n: number, i: number) => ReactNode, ayuda?: string) => (
    <tr key={k + clave}><th scope="row" className="perio-et" title={ayuda}>{titulo}</th>{celdas(clave, f)}</tr>
  );
  const marca = ([cara, nombre]: ["v" | "l", string], campo: "sang" | "placa" | "sup", titulo: string) =>
    fila(`${cara}${campo}`, titulo, (n, i) => <Marca clase={campo} activa={datos[n][cara][i][campo]} bloqueado={bloqueado} etiqueta={`${titulo} ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={() => sitio(n, cara, i, (s) => ({ ...s, [campo]: !s[campo] }))} />);
  const sondaje = ([cara, nombre]: ["v" | "l", string]) =>
    fila(`${cara}pd`, "Profundidad de sondaje", (n, i) => { const s = datos[n][cara][i]; return <Num valor={s.pd} min={0} max={15} bloqueado={bloqueado} clase={s.pd !== null && s.pd >= 6 ? "grave" : s.pd !== null && s.pd >= 4 ? "alto" : ""} etiqueta={`Sondaje ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={(x) => sitio(n, cara, i, (y) => ({ ...y, pd: x }))} />; });
  const recesion = ([cara, nombre]: ["v" | "l", string]) =>
    fila(`${cara}rec`, "Recesión", (n, i) => <Num valor={datos[n][cara][i].rec} min={-5} max={15} bloqueado={bloqueado} etiqueta={`Recesión ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={(x) => sitio(n, cara, i, (s) => ({ ...s, rec: x }))} />,
      "Recesión en mm: positivo = el margen está por debajo del límite amelocementario; negativo = por encima");
  const porDiente = (clave: string, titulo: string, f: (n: number) => ReactNode) => (
    <tr key={k + clave}><th scope="row" className="perio-et">{titulo}</th>{dientes.map((n) => <td key={n} colSpan={3} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && f(n)}</td>)}</tr>
  );
  const movilidad = porDiente("mov", "Movilidad", (n) => <button type="button" className="perio-ciclo" disabled={bloqueado} aria-label={`Movilidad del ${n}`} onClick={() => cambiar(n, (d) => ({ ...d, mov: (d.mov + 1) % 4 }))}>{datos[n].mov || "0"}</button>);
  const furca = porDiente("furca", "Furcación", (n) => tieneFurca(n) && <button type="button" className="perio-ciclo" disabled={bloqueado} aria-label={`Furca del ${n}`} onClick={() => cambiar(n, (d) => ({ ...d, furca: (d.furca + 1) % 4 }))}>{datos[n].furca || "0"}</button>);
  const imagen = (
    <tr key={k + "img"} className="perio-img"><th scope="row" className="perio-et perio-et-img">{nombreCara}</th>
      <td colSpan={cols}><DientesCara dientes={dientes} datos={datos} cara={caraPedida} raizAbajo={inferior} segunda={caraPedida === "l"} nombre={nombreCara} /></td></tr>
  );
  const numeros = (
    <tr key={k + "num"} className="perio-nums"><th scope="row" className="perio-et">Diente</th>
      {dientes.map((n) => (
        <td key={n} colSpan={3} className={datos[n].ausente ? "perio-aus" : ""}>
          <button type="button" className="perio-dte" disabled={bloqueado} aria-pressed={datos[n].ausente} title={datos[n].ausente ? "Ausente: toca para marcar presente" : "Toca para marcar ausente"} onClick={() => cambiar(n, (d) => ({ ...d, ausente: !d.ausente }))}>{n}</button>
        </td>
      ))}</tr>
  );
  // De afuera hacia los dientes: sangrado, placa, supuración, (movilidad), furcación, sondaje, recesión. La movilidad es del diente: sale solo en la cara vestibular.
  const exterior = [marca(primera, "sang", "Sangrado"), marca(primera, "placa", "Placa"), marca(primera, "sup", "Supuración"), ...(caraPedida === "v" ? [movilidad] : []), furca, sondaje(primera), recesion(primera)];
  return { exterior: inferior ? exterior.reverse() : exterior, imagen, numeros, cols };
}

/**
 * Una tarjeta por cara, con las dos arcadas juntas: arriba los dientes superiores (raíz hacia arriba), en medio los números y abajo los inferiores (raíz hacia abajo).
 * Cara vestibular: vestibular superior e inferior. Cara palatina/lingual: palatina de los superiores y lingual de los inferiores.
 */
export function Cara({ cara, datos, cambiar, bloqueado }: { cara: "v" | "l"; datos: DatosPerio; cambiar: (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => void; bloqueado: boolean }) {
  const sup = filasArcada({ dientes: DIENTES_SUPERIORES, datos, cambiar, bloqueado, inferior: false, cara });
  const inf = filasArcada({ dientes: DIENTES_INFERIORES, datos, cambiar, bloqueado, inferior: true, cara });
  const cols = sup.cols;
  return (
    <div className="perio-scroll">
      <table className="perio-tabla" style={{ width: ETIQ + cols * W }} onKeyDown={(e) => {
        if (e.key !== "Shift" || e.repeat) return;
        const todas = [...document.querySelectorAll<HTMLElement>(".perio-tabla input.perio-n:not(:disabled), .perio-tabla button.perio-m:not(:disabled), .perio-tabla button.perio-ciclo:not(:disabled)")];
        const i = todas.indexOf(e.target as HTMLElement);
        if (i >= 0) { e.preventDefault(); todas[i + 1]?.focus(); }
      }}>
        <colgroup><col style={{ width: ETIQ }} />{Array.from({ length: cols }, (_, i) => <col key={i} style={{ width: W }} />)}</colgroup>
        <tbody>
          <tr className="perio-titulo"><th colSpan={1 + cols}>{cara === "v" ? "Cara vestibular · examinar" : "Cara palatina (superiores) y lingual (inferiores) · examinar"}</th></tr>
          {sup.exterior}
          {sup.imagen}
          {sup.numeros}
          {inf.numeros}
          {inf.imagen}
          {inf.exterior}
        </tbody>
      </table>
    </div>
  );
}

function Resumen({ r }: { r: ResumenPerio }) {
  const casilla = (t: string, v: string, grave = false) => <span className={`chip ${grave ? "rojo" : ""}`}>{t}: <b>{v}</b></span>;
  return (
    <div className="fila" style={{ flexWrap: "wrap", gap: 6 }} aria-live="polite">
      {casilla("Dientes", String(r.dientes))}
      {casilla("Sitios medidos", String(r.sitios))}
      {casilla("Sondaje medio", r.pdMedia === null ? "—" : `${r.pdMedia} mm`)}
      {casilla("NIC medio", r.nicMedia === null ? "—" : `${r.nicMedia} mm`)}
      {casilla("Sitios ≥ 4 mm", String(r.sitios4), r.sitios4 > 0)}
      {casilla("Sitios ≥ 6 mm", String(r.sitios6), r.sitios6 > 0)}
      {casilla("Sangrado", r.pctSangrado === null ? "—" : `${r.pctSangrado} %`, (r.pctSangrado ?? 0) > 30)}
      {casilla("Placa", r.pctPlaca === null ? "—" : `${r.pctPlaca} %`, (r.pctPlaca ?? 0) > 30)}
      {r.supuracion > 0 && casilla("Supuración", `${r.supuracion} sitios`, true)}
      {r.conMovilidad > 0 && casilla("Con movilidad", String(r.conMovilidad))}
      {r.conFurca > 0 && casilla("Con furca", String(r.conFurca))}
    </div>
  );
}

export function Periodontograma({ paciente }: { paciente: Paciente }) {
  const [lista, setLista] = useState<Registro[] | null>(null);
  const [datos, setDatos] = useState<DatosPerio | null>(null);
  const [viendo, setViendo] = useState<Registro | null>(null);
  const [fecha, setFecha] = useState(hoy());
  const [obs, setObs] = useState("");
  const a = useAccion();
  const url = `/api/pacientes/${paciente.id}/periodontogramas`;
  useEffect(() => { setLista(null); setDatos(null); setViendo(null); api<{ periodontogramas: Registro[] }>("GET", url).then((r) => setLista(r.periodontogramas)).catch(a.setError); }, [paciente.id]);

  const limpio = useMemo(() => (datos ? limpiarPeriodontograma(datos) : null), [datos]);
  const resumen = useMemo(() => (limpio ? resumenPeriodontograma(limpio) : null), [limpio]);
  const problemas = limpio ? problemasPeriodontograma(limpio) : [];
  const editable = !!datos && !viendo;

  const nuevo = () => {
    // los dientes que ya faltaban en el último registro se dejan como ausentes
    const base = periodontogramaVacio();
    const ultimo = lista?.[0];
    if (ultimo) for (const n of Object.keys(base)) if (ultimo.datos[n]?.ausente) base[n].ausente = true;
    setDatos(base); setViendo(null); setObs(""); setFecha(hoy());
  };
  const cambiar = (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => setDatos((d) => (d ? { ...d, [String(n)]: f(d[String(n)]) } : d));
  const guardar = async () => {
    const r = await a.correr(() => api("POST", url, { fecha, observaciones: obs, datos: limpio }));
    if (!r) return;
    const x = await api<{ periodontogramas: Registro[] }>("GET", url);
    setLista(x.periodontogramas); setDatos(null);
  };

  if (!lista) return <AvisoError error={a.error} />;
  const mostrado = viendo ? viendo.datos : datos;
  return (
    <Seccion titulo="Periodontograma" acciones={puede("clinico:escribir") && !datos && <button className="btn chico pri" onClick={nuevo}>Nuevo periodontograma</button>}>
      {!mostrado && lista.length === 0 && <p className="muted" style={{ margin: 0 }}>Sin periodontogramas registrados. Cada uno se guarda como un registro nuevo para poder comparar la evolución.</p>}
      {mostrado && (
        <>
          <div className="fila" style={{ flexWrap: "wrap", alignItems: "end" }}>
            {viendo
              ? <b>Periodontograma del {fechaLarga(viendo.fecha)} · {viendo.profesional}</b>
              : <label className="campo" style={{ margin: 0 }}>Fecha<FechaInput max={hoy()} value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>}
            {viendo && <button className="btn chico" onClick={() => setViendo(null)}>Cerrar</button>}
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Llena fila por fila: <b>Shift</b> pasa a la casilla siguiente y <b>Enter</b> cambia el valor (en las casillas de números sube de uno en uno y, al pasar del máximo, vuelve a 0; en placa, sangrado y supuración marca o desmarca). También puedes escribir el número. Toca el número del diente para marcarlo ausente; movilidad y furca cambian de 0 a 3 con cada toque. Sondaje de 4 mm o más sale en ámbar y de 6 mm o más en rojo.
            El nivel de inserción (NIC) medio aparece en el resumen de abajo.
            El dibujo de los dientes se va llenando solo: la línea gris es el margen de la encía (recesión) y la línea de color es el fondo del surco (margen + sondaje): <b style={{ color: "#2e9e5b" }}>verde</b> sin hallazgo, <b style={{ color: "#8b5a2b" }}>marrón</b> con sangrado o sondaje de 4 a 5 mm, <b style={{ color: "#d13b3b" }}>rojo</b> con supuración o sondaje de 6 mm o más.
          </p>
          <Cara cara="v" datos={mostrado} cambiar={cambiar} bloqueado={!editable} />
          <Cara cara="l" datos={mostrado} cambiar={cambiar} bloqueado={!editable} />
          <Resumen r={viendo ? viendo.resumen : resumen!} />
          {viendo?.observaciones && <p style={{ margin: 0 }}>{viendo.observaciones}</p>}
          {editable && (
            <>
              <label className="campo">Observaciones (opcional)<textarea rows={2} maxLength={1000} value={obs} onChange={(e) => setObs(e.target.value)} /></label>
              {problemas.length > 0 && <div className="aviso info"><ul>{problemas.map((x) => <li key={x}>{x}</li>)}</ul></div>}
              <AvisoError error={a.error} />
              <div className="fila">
                <button className="btn pri" disabled={a.ocupado || problemas.length > 0} onClick={guardar}>Guardar periodontograma</button>
                <button className="btn" onClick={() => setDatos(null)}>Cancelar</button>
              </div>
              <small className="muted">Al guardarlo queda como registro fijo; si te equivocas, haz uno nuevo.</small>
            </>
          )}
        </>
      )}
      {lista.length > 0 && !datos && (
        <>
          <h3>Registros anteriores</h3>
          <ul className="lista">
            {lista.map((x, i) => {
              const anterior = lista[i + 1];
              const c = anterior ? compararPerio(anterior.resumen, x.resumen) : null;
              return (
                <li key={x.id}>
                  <button onClick={() => setViendo(x)} aria-current={viendo?.id === x.id}>
                    <span><b>{fechaLarga(x.fecha)}</b> · {x.profesional}<br /><span className="muted" style={{ fontSize: 13 }}>{textoResumenPerio(x.resumen)}</span>
                      {c && <><br /><span style={{ fontSize: 13 }}>Frente al anterior: {[signo(c.pdMedia, "mm de sondaje medio"), signo(c.pctSangrado, "puntos de sangrado"), signo(c.sitios4 || null, "sitios ≥ 4 mm")].filter(Boolean).join(" · ") || "sin diferencias"}</span></>}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {!mostrado && <AvisoError error={a.error} />}
    </Seccion>
  );
}
